import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import { useEffect } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Markdown from 'react-native-markdown-display';
import { PremiumFooter } from '../../src/components/landing/PremiumFooter';
import { Container } from '../../src/components/primitives/Container';
import { Text } from '../../src/components/primitives/Text';
import { useTranslation } from '../../src/contexts/TranslationContext';
import { trackEvent } from '../../src/lib/analytics';
import type { ChangelogEntry } from '../../src/lib/api';
import { changelogAPI } from '../../src/lib/api';
import { useMixpanel } from '../../src/lib/mixpanel';
import { colors } from '../../src/theme/colors';
import { spacing } from '../../src/theme/spacing';

// ---------------------------------------------------------------------------
// Web-only route: /changelog/:slug
// Renders a single published changelog entry with markdown body.
// ---------------------------------------------------------------------------

function TagBadge({ label }: { label: string }) {
  return (
    <View style={styles.tag}>
      <Text variant="caption" color={colors.primary[500]} style={styles.tagText}>
        {label}
      </Text>
    </View>
  );
}

const markdownStyles = StyleSheet.create({
  body: {
    color: colors.text.primary,
    fontSize: Platform.select({ web: 17, default: 15 }),
    lineHeight: Platform.select({ web: 28, default: 24 }),
  },
  heading1: {
    color: colors.text.primary,
    fontSize: Platform.select({ web: 28, default: 24 }),
    fontWeight: '700',
    marginTop: spacing.xl,
    marginBottom: spacing.sm,
  },
  heading2: {
    color: colors.text.primary,
    fontSize: Platform.select({ web: 22, default: 20 }),
    fontWeight: '700',
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  heading3: {
    color: colors.text.primary,
    fontSize: Platform.select({ web: 18, default: 17 }),
    fontWeight: '600',
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  paragraph: {
    color: colors.text.secondary,
    marginBottom: spacing.md,
  },
  code_inline: {
    backgroundColor: colors.background.secondary,
    color: colors.primary[600],
    paddingHorizontal: 4,
    borderRadius: 3,
  },
  code_block: {
    backgroundColor: colors.background.secondary,
    padding: spacing.md,
    borderRadius: 6,
    marginBottom: spacing.md,
  },
  fence: {
    backgroundColor: colors.background.secondary,
    padding: spacing.md,
    borderRadius: 6,
    marginBottom: spacing.md,
  },
  bullet_list: {
    marginBottom: spacing.md,
  },
  ordered_list: {
    marginBottom: spacing.md,
  },
  link: {
    color: colors.primary[500],
  },
  blockquote: {
    borderLeftWidth: 3,
    borderLeftColor: colors.primary[300],
    paddingLeft: spacing.md,
    marginBottom: spacing.md,
  },
  hr: {
    backgroundColor: colors.border.muted,
    height: 1,
    marginVertical: spacing.lg,
  },
});

export default function ChangelogDetailPage() {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const router = useRouter();
  const { t } = useTranslation();
  const mp = useMixpanel();

  const {
    data: entry,
    isLoading,
    isError,
  } = useQuery<ChangelogEntry>({
    queryKey: ['changelog-entry', slug],
    queryFn: async () => {
      const response = await changelogAPI.getEntry(slug);
      return response.data;
    },
    enabled: !!slug,
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (entry && !isLoading) {
      trackEvent(mp, 'changelog.entry_viewed', {
        slug: entry.slug,
        has_version: !!entry.version,
        tag_count: entry.tags.length,
      });
    }
  }, [entry?.slug]);

  const dateLabel = entry?.publishedAt ? format(new Date(entry.publishedAt), 'MMMM d, yyyy') : null;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Container maxWidth="md">
        <Pressable
          testID="changelog-detail-back"
          onPress={() => router.push('/changelog')}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel={t('changelog.backToChangelog')}
        >
          <ArrowLeft size={20} color={colors.primary[500]} />
          <Text variant="bodySmall" color={colors.primary[500]}>
            {t('changelog.backToChangelog')}
          </Text>
        </Pressable>

        {isLoading ? (
          <View testID="changelog-detail-loading" style={styles.stateContainer}>
            <Text variant="body" color={colors.text.secondary}>
              {t('changelog.loading')}
            </Text>
          </View>
        ) : isError ? (
          <View testID="changelog-detail-error" style={styles.stateContainer}>
            <Text variant="body" color={colors.status.error.main}>
              {t('changelog.error')}
            </Text>
          </View>
        ) : entry ? (
          <>
            <View style={styles.entryHeader}>
              <View style={styles.metaRow}>
                {entry.version ? (
                  <View testID="changelog-detail-version" style={styles.versionBadge}>
                    <Text variant="caption" color={colors.primary[600]} style={styles.versionText}>
                      {t('changelog.version')}
                      {entry.version}
                    </Text>
                  </View>
                ) : null}
                {dateLabel ? (
                  <Text variant="caption" color={colors.text.tertiary}>
                    {t('changelog.publishedOn')} {dateLabel}
                  </Text>
                ) : null}
              </View>

              <Text variant="h1" style={styles.entryTitle} accessibilityRole="header">
                {entry.title}
              </Text>

              {entry.tags.length > 0 ? (
                <View style={styles.tagsRow}>
                  {entry.tags.map((tag) => (
                    <TagBadge key={tag} label={tag} />
                  ))}
                </View>
              ) : null}
            </View>

            <View style={styles.divider} />

            <View testID="changelog-detail-body">
              <Markdown style={markdownStyles}>{entry.body}</Markdown>
            </View>
          </>
        ) : null}
      </Container>

      <PremiumFooter />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    flexGrow: 1,
    paddingTop: Platform.select({ web: 60, default: 40 }),
    paddingBottom: spacing.xxl,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xl,
    alignSelf: 'flex-start',
  },
  stateContainer: {
    paddingVertical: spacing.xxl,
    alignItems: 'center',
  },
  entryHeader: {
    marginBottom: spacing.lg,
    gap: spacing.sm,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  versionBadge: {
    backgroundColor: colors.primary[50],
    borderRadius: 4,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: colors.primary[200],
  },
  versionText: {
    fontWeight: '600',
  },
  entryTitle: {
    fontSize: Platform.select({ web: 42, default: 28 }),
    fontWeight: '700',
    letterSpacing: -0.5,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  tag: {
    backgroundColor: colors.background.secondary,
    borderRadius: 4,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
  },
  tagText: {
    fontWeight: '500',
  },
  divider: {
    height: 1,
    backgroundColor: colors.border.muted,
    marginBottom: spacing.xxl,
  },
});
